import { AnyAction, CombinedState, combineReducers } from 'redux'
import { HYDRATE } from 'next-redux-wrapper'

import cart from './cart/cart'
import dashboard from './dashboard/dashboard'
import draft from './draft/draft'
import entitlements from './entitlement/entitlement'
import global from './global/global'
import jobs from './jobs/jobs'
import packages from './packages/packages'
import ui from './ui/ui'
import user from './user/user'
import webshop from './webshop/webshop'

import jobContentToDraft from '../../service/util/job-content-to-draft/jobContentToDraft'
import type { CartStoreType } from './cart/cart'
import type { DashboardStoreType } from './dashboard/dashboard'
import type { DraftStoreType } from './draft/draft'
import type {
  EntitlementStoreType,
  JobPostingProduct,
  ProductUpgrade,
  ProfileViewProduct,
  PerformanceBasedProduct,
} from './entitlement/entitlement'
import type { GlobalStoreType } from './global/global'
import type {
  JobContent,
  JobsStoreType,
  UpgradedJobPostingProduct,
} from './jobs/jobs'
import type { PackagesStoreType } from './packages/packages'
import type { UiStoreType } from './ui/ui'
import type { DashboardUser } from '../../schema/user'
import type { WebshopStoreType } from './webshop/webshop'
import type { SiteAlias } from '../../schema/webshop-package'

export type ProductConfig =
  | JobPostingProduct
  | ProductUpgrade
  | ProfileViewProduct
  | UpgradedJobPostingProduct
  | PerformanceBasedProduct

export type JobPostingDefinition = Omit<JobPostingProduct | UpgradedJobPostingProduct, 'name' | 'type'>

export type UpgradeDefinition = {
  features: Array<string>
  sites: Array<SiteAlias>
}

export type UpgradeRequirements = {
  requiredFeatures: Array<string>
  requiredSites: Array<SiteAlias>
}

export type ProductDefinition = JobPostingDefinition & UpgradeDefinition

export type State = {
  cart: CartStoreType
  dashboard: DashboardStoreType
  draft: DraftStoreType
  entitlements: EntitlementStoreType
  global: GlobalStoreType
  jobs: JobsStoreType
  packages: PackagesStoreType
  ui: UiStoreType
  user: DashboardUser
  webshop: WebshopStoreType
}

export const initialJobContent: JobContent = {
  content: {
    title: '',
    description: '',
    functionGroup: {
      unclassified: ''
    },
    jobCategories: [],
    industryCategories: [],
  },
  criteria: {
    educationLevels: [],
    careerLevels: [],
  },
  contract: {
    contractTypes: [],
    workingHours: {
      lower: 32,
      upper: 40,
    },
  },
  publicationPeriod: {
    start: '',
    end: '',
  },
  location: {
    country: 'NL',
    postalCode: '',
  },
  applyVia: {
    via: 'jobboard',
    applyOption: 'ByEmail',
    emailAddress: '',
    firstName: '',
    lastName: '',
  },
  customFields: [],
}

export const product: JobPostingProduct = {
  type: 'jobposting',
  name: '',
  features: [],
  publishOn: [],
  daysOnline: 60,
  predefined: {},
}

export const userState: DashboardUser = {
  isInitialized: false,
  firstName: '',
  lastName: '',
  referenceId: '',
  phoneNumber: '',
  emailAddress: '',
  company: {
    name: '',
    referenceId: '',
    alternativeNames: [],
    companyType: '',
  },
  title: '',
  isVerified: false,
  consentedForMarketingContact: false,
  consentedForPhoneCalls: null,
  telecomLawApplicable: true,
  consentedForJobExpiryEmail: true,
}

export const initialState: State = {
  cart: {
    packages: [],
    payment: {
      status: '',
      statusMessage: '',
      basketId: '',
    },
  },
  dashboard: {
    recentlyPostedJobs: [],
    recentlyPostedJobsAreLoaded: false,
    statisticsOfRecentlyPostedJobs: {},
    recentlyCreatedDrafts: [],
    jobsWithErrors: [],
    lastRejectedJob: undefined,
  },
  draft: {
    draft: jobContentToDraft({
      jobContent: initialJobContent,
      product,
      user: userState,
      userCanTypeAlternativeContactInfo: false,
    }),
    id: '',
    product,
    isConfirmed: false,
    status: 'draft',
    upgrades: [],
  },
  entitlements: {
    jobPostings: [],
    jobUpgrades: [],
    profileViews: [],
    performanceBased: [],
    upgradeCredits: {},
    entitlementsAreLoaded: false,
  },
  global: {
    features: {},
  },
  jobs: {
    jobs: [],
    pagination: {
      filterAmounts: [],
    },
    statistics: {},
    statisticsFailed: false,
  },
  packages: {
    suggestiblePackages: [],
  },
  ui: {
    flashMessage: {
      type: '',
    },
    modal: {
      title: '',
      content: '',
      showDialog: false,
      isPending: false,
    },
    job: {
      status: {},
      currentPage: 1,
      currentFilter: null,
      currentFilters: [],
      currentSearchQuery: '',
      isFetchingAll: false,
      isCreating: false,
      isSubmitting: false,
      isLogoUploading: false,
      isAboutToBeEdited: false,
      totalPageCount: 0,
      jobIdWithShownUpgrades: null,
      requestedJobUpgrades: {},
    },
    user: {
      isSubmittingInfo: false,
      isSubmittingSettings: false,
    },
    account: {
      isDefaultLogoUploading: false,
    },
    suggestions: {
      selectedSites: [],
    },
    inProgressElementId: '',
    displayProductModal: false,
    toasters: [],
    suggestedSalaryMin: null,
    suggestedSalaryMax: null,
  },
  user: userState,
  webshop: {
    inOrder: [],
    vatTotal: 0,
    packages: [],
    hiddenPackages: [],
    status: '',
    getBasket: '',
    removeProduct: {
      status: '',
    }
  },
}

const combinedReducer = combineReducers<State>({
  cart,
  dashboard,
  draft,
  entitlements,
  global,
  jobs,
  packages,
  ui,
  user,
  webshop,
})

const reducers = (state: State = initialState, action: AnyAction): CombinedState<State> => {
  // See next-redux-wrapper "State reconciliation during hydration" for more information
  // https://github.com/kirill-konshin/next-redux-wrapper#state-reconciliation-during-hydration

  if (action.type === HYDRATE) {
    const nextState = {
      ...state, // use previous state
      ...action.payload, // apply delta from hydration
    }

    // preserve state on client-side navigation
    nextState.cart = state.cart
    nextState.draft = state.draft
    nextState.packages = state.packages
    nextState.ui = state.ui

    return nextState
  }

  return combinedReducer(state, action)
}

export default reducers
